import { DataBlockTypeDto, SexType } from '@lmt/shared-types';
import { MeasuredValue } from '@lmt/unicorn-shared/app/features/measurements/types';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { checkMiniScreenPdfFile } from './file-operations';
import { parseDate } from './parsing';

const miniscreenFields: Record<string, string> = {
  patientNumber: '0.FG.0.3',
  template: '0.OR.0.1',
  pdfFile: '0.OR.0.0',
  firstName: '0.FG.0.1',
  lastName: '0.FG.0.0',
  startDate: '0.FG.1.0',
  endDate: '0.FG.1.1',
  dateOfBirth: '0.FG.0.2',
  sex: '0.FG.0.6',
};

export interface PatientInfo {
  number: string;
  firstName: string;
  lastName: string;
  startDate: Date;
  endDate: Date;
  dateOfBirth: Date | null;
  sex: SexType | null;
}

export async function getPatientInfoFromMeasuredValues(
  measuredValues: MeasuredValue[],
): Promise<PatientInfo> {
  return {
    number: getMeasuredValue(
      measuredValues,
      miniscreenFields.patientNumber,
      'patient number',
    ),
    firstName: getMeasuredValue(
      measuredValues,
      miniscreenFields.firstName,
      'patient first name',
    ),
    lastName: getMeasuredValue(
      measuredValues,
      miniscreenFields.lastName,
      'patient last name',
    ),
    startDate: parseDate(
      getMeasuredValue(
        measuredValues,
        miniscreenFields.startDate,
        'measurement start time',
      ),
    ),
    endDate: parseDate(
      getMeasuredValue(
        measuredValues,
        miniscreenFields.endDate,
        'measurement end time',
      ),
    ),
    dateOfBirth: getNullableDateValue(
      measuredValues,
      miniscreenFields.dateOfBirth,
      'patient date of birth',
    ),
    sex: getSexValue(
      getMeasuredValue(measuredValues, miniscreenFields.sex, 'patient sex'),
    ),
  };
}

export function getTemplateName(measuredValues: MeasuredValue[]) {
  return getMeasuredValue(
    measuredValues,
    miniscreenFields.template,
    'Template name',
  );
}

function getMeasuredValue(
  measuredValues: MeasuredValue[],
  code: string,
  errorTextKey: string,
) {
  const measuredValue = measuredValues.find(
    (measuredValue) => measuredValue.key === code,
  );
  if (measuredValue) {
    return measuredValue.value;
  }

  throw Error(`Can't find ${errorTextKey} in measurement file.`);
}

function getNullableDateValue(
  measuredValues: MeasuredValue[],
  code: string,
  errorTextKey: string,
) {
  const value = getMeasuredValue(measuredValues, code, errorTextKey);
  if (value) {
    return parseDate(value);
  }
  return null;
}

export function getTemplateMeasurementType(
  measuredValues: MeasuredValue[],
  types: DataBlockTypeDto[],
): DataBlockTypeDto | undefined {
  const templateMeasuredValue = measuredValues.find(
    (measuredValue) => measuredValue.key === miniscreenFields.template,
  );
  if (templateMeasuredValue && templateMeasuredValue.value) {
    const template = templateMeasuredValue.value.toLowerCase();
    const measurementType = types.find((type) =>
      template.includes(type.value.toLowerCase()),
    );
    return measurementType;
  }
  return undefined;
}

export async function getPdfFilePath(
  measuredValues: MeasuredValue[],
): Promise<string | undefined> {
  if (isElectron()) {
    const templateMeasuredValue = measuredValues.find(
      (measuredValue) => measuredValue.key === miniscreenFields.pdfFile,
    );
    if (templateMeasuredValue && templateMeasuredValue.value) {
      const result = await checkMiniScreenPdfFile(templateMeasuredValue.value);
      return result.type === 'success'
        ? templateMeasuredValue.value
        : undefined;
    }
  }
  return undefined;
}

function getSexValue(value: string): SexType | null {
  return value === 'm' ? SexType.Male : value === 'f' ? SexType.Female : null;
}
